"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from "react";
import type { CreateWaveConfig, CreateWaveOutcomeType, TimeWeightedVotingSettings } from "@/types/waves.types";
import { CreateWaveGroupConfigType, CreateWaveStep } from "@/types/waves.types";
import { ApiWaveType } from "@/generated/models/ApiWaveType";
import { Time } from "@/helpers/time";
import type { ApiGroupFull } from "@/generated/models/ApiGroupFull";
import { ApiWaveCreditType } from "@/generated/models/ApiWaveCreditType";
import type { Period } from "../types/period";
import type { CREATE_WAVE_VALIDATION_ERROR } from "@/helpers/waves/create-wave.validation";
import { getCreateWaveValidationErrors } from "@/helpers/waves/create-wave.validation";
import { assertUnreachable } from "@/helpers/AllowlistToolHelpers";
interface EndDateConfig {
  time: number | null;
  period: Period | null;
}
export function useWaveConfig() {
  const $ = _c(38);
  const initialType = ApiWaveType.Chat;
  const initialStep = CreateWaveStep.OVERVIEW;
  const getInitialConfig = _temp;
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = getInitialConfig({
      type: initialType
    });
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [config, setConfig] = useState(t0);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      time: null,
      period: null
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [endDateConfig, setEndDateConfig] = useState(t1);
  const [step, setStep] = useState(initialStep);
  const [selectedOutcomeType, setSelectedOutcomeType] = useState(null);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const [errors, setErrors] = useState(t2);
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {};
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const [groupsCache, setGroupsCache] = useState(t3);
  let t4;
  let t5;
  if ($[4] !== config.dates.endDate) {
    t4 = () => {
      if (config.dates.endDate === null) {
        setEndDateConfig({
          time: null,
          period: null
        });
      }
    };
    t5 = [config.dates.endDate];
    $[4] = config.dates.endDate;
    $[5] = t4;
    $[6] = t5;
  } else {
    t4 = $[5];
    t5 = $[6];
  }
  useEffect(t4, t5);
  let t6;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      setErrors([]);
    };
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== config) {
    t7 = [config];
    $[8] = config;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  useEffect(t6, t7);
  let t8;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = overview => {
      setEndDateConfig({
        time: null,
        period: null
      });
      setConfig(() => ({
        ...getInitialConfig({
          type: overview.type
        }),
        overview
      }));
    };
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  const setOverview = t8;
  let t9;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = dates => {
      setConfig(prev => ({
        ...prev,
        dates
      }));
    };
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  const setDates = t9;
  let t10;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = drops => {
      setConfig(prev_0 => ({
        ...prev_0,
        drops
      }));
    };
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  const setDrops = t10;
  let t11;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = adminCanDeleteDrops => {
      setConfig(prev_1 => ({
        ...prev_1,
        drops: {
          ...prev_1.drops,
          adminCanDeleteDrops
        }
      }));
    };
    $[13] = t11;
  } else {
    t11 = $[13];
  }
  const setDropsAdminCanDelete = t11;
  let t12;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = outcomes => {
      setConfig(prev_2 => ({
        ...prev_2,
        outcomes
      }));
    };
    $[14] = t12;
  } else {
    t12 = $[14];
  }
  const setOutcomes = t12;
  let t13;
  if ($[15] !== config || $[16] !== step) {
    t13 = t14 => {
      const {
        step: newStep,
        direction
      } = t14;
      if (direction === "forward") {
        const newErrors = getCreateWaveValidationErrors({
          config,
          step
        });
        if (newErrors.length) {
          setErrors(newErrors);
          return;
        }
      }
      setErrors([]);
      setSelectedOutcomeType(null);
      setStep(newStep);
    };
    $[15] = config;
    $[16] = step;
    $[17] = t13;
  } else {
    t13 = $[17];
  }
  const onStep = t13;
  let t14;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = outcomeType => {
      setSelectedOutcomeType(outcomeType);
      setErrors([]);
    };
    $[18] = t14;
  } else {
    t14 = $[18];
  }
  const onOutcomeTypeChange = t14;
  let t15;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = t16 => {
      const {
        group,
        groupType
      } = t16;
      if (group) {
        setGroupsCache(prev_3 => ({
          ...prev_3,
          [group.id]: group
        }));
      }
      bb38: switch (groupType) {
        case CreateWaveGroupConfigType.CAN_VIEW:
          {
            setConfig(prev_8 => ({
              ...prev_8,
              groups: {
                ...prev_8.groups,
                canView: group?.id ?? null
              }
            }));
            break bb38;
          }
        case CreateWaveGroupConfigType.CAN_DROP:
          {
            setConfig(prev_7 => ({
              ...prev_7,
              groups: {
                ...prev_7.groups,
                canDrop: group?.id ?? null
              }
            }));
            break bb38;
          }
        case CreateWaveGroupConfigType.CAN_VOTE:
          {
            setConfig(prev_6 => ({
              ...prev_6,
              groups: {
                ...prev_6.groups,
                canVote: group?.id ?? null
              }
            }));
            break bb38;
          }
        case CreateWaveGroupConfigType.CAN_CHAT:
          {
            setConfig(prev_5 => ({
              ...prev_5,
              groups: {
                ...prev_5.groups,
                canChat: group?.id ?? null
              }
            }));
            break bb38;
          }
        case CreateWaveGroupConfigType.ADMIN:
          {
            setConfig(prev_4 => ({
              ...prev_4,
              groups: {
                ...prev_4.groups,
                admin: group?.id ?? null
              }
            }));
            break bb38;
          }
        default:
          {
            assertUnreachable(groupType);
          }
      }
    };
    $[19] = t15;
  } else {
    t15 = $[19];
  }
  const onGroupSelect = t15;
  let t16;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = type_0 => {
      setConfig(prev_9 => ({
        ...prev_9,
        voting: {
          type: type_0,
          category: null,
          profileId: null,
          maxVotesPerIdentityPerDrop: prev_9.voting.maxVotesPerIdentityPerDrop,
          winningThreshold: prev_9.voting.winningThreshold,
          timeWeighted: prev_9.voting.timeWeighted
        }
      }));
    };
    $[20] = t16;
  } else {
    t16 = $[20];
  }
  const onVotingTypeChange = t16;
  let t17;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = timeWeighted => {
      setConfig(prev_10 => ({
        ...prev_10,
        voting: {
          ...prev_10.voting,
          timeWeighted
        }
      }));
    };
    $[21] = t17;
  } else {
    t17 = $[21];
  }
  const onTimeWeightedVotingChange = t17;
  let t18;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = enabled => {
      setConfig(prev_11 => ({
        ...prev_11,
        chat: {
          enabled
        }
      }));
    };
    $[22] = t18;
  } else {
    t18 = $[22];
  }
  const onChatEnabledChange = t18;
  let t19;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = category => {
      setConfig(prev_12 => ({
        ...prev_12,
        voting: {
          ...prev_12.voting,
          category
        }
      }));
    };
    $[23] = t19;
  } else {
    t19 = $[23];
  }
  const onCategoryChange = t19;
  let t20;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = profileId => {
      setConfig(prev_13 => ({
        ...prev_13,
        voting: {
          ...prev_13.voting,
          profileId
        }
      }));
    };
    $[24] = t20;
  } else {
    t20 = $[24];
  }
  const onProfileIdChange = t20;
  let t21;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = maxVotesPerIdentityPerDrop => {
      setConfig(prev_14 => ({
        ...prev_14,
        voting: {
          ...prev_14.voting,
          maxVotesPerIdentityPerDrop
        }
      }));
    };
    $[25] = t21;
  } else {
    t21 = $[25];
  }
  const onMaxVotesPerIdentityPerDropChange = t21;
  let t22;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = winningThreshold => {
      setConfig(prev_15 => ({
        ...prev_15,
        voting: {
          ...prev_15.voting,
          winningThreshold
        }
      }));
    };
    $[26] = t22;
  } else {
    t22 = $[26];
  }
  const onWinningThresholdChange = t22;
  let t23;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = threshold => {
      setConfig(prev_16 => ({
        ...prev_16,
        approval: {
          ...prev_16.approval,
          threshold
        }
      }));
    };
    $[27] = t23;
  } else {
    t23 = $[27];
  }
  const onThresholdChange = t23;
  let t24;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = thresholdTimeMs => {
      setConfig(prev_17 => ({
        ...prev_17,
        approval: {
          ...prev_17.approval,
          thresholdTimeMs
        }
      }));
    };
    $[28] = t24;
  } else {
    t24 = $[28];
  }
  const onThresholdTimeChange = t24;
  let t25;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = maxWinners => {
      setConfig(prev_18 => ({
        ...prev_18,
        approval: {
          ...prev_18.approval,
          maxWinners
        }
      }));
    };
    $[29] = t25;
  } else {
    t25 = $[29];
  }
  const onApprovalMaxWinnersChange = t25;
  let t26;
  if ($[30] !== config || $[31] !== endDateConfig || $[32] !== errors || $[33] !== groupsCache || $[34] !== onStep || $[35] !== selectedOutcomeType || $[36] !== step) {
    t26 = {
      config,
      setConfig,
      endDateConfig,
      setEndDateConfig,
      step,
      selectedOutcomeType,
      errors,
      groupsCache,
      setOverview,
      setDates,
      setDrops,
      setDropsAdminCanDelete,
      setOutcomes,
      onStep,
      onOutcomeTypeChange,
      onGroupSelect,
      onVotingTypeChange,
      onCategoryChange,
      onProfileIdChange,
      onMaxVotesPerIdentityPerDropChange,
      onTimeWeightedVotingChange,
      onWinningThresholdChange,
      onThresholdChange,
      onThresholdTimeChange,
      onApprovalMaxWinnersChange,
      onChatEnabledChange
    };
    $[30] = config;
    $[31] = endDateConfig;
    $[32] = errors;
    $[33] = groupsCache;
    $[34] = onStep;
    $[35] = selectedOutcomeType;
    $[36] = step;
    $[37] = t26;
  } else {
    t26 = $[37];
  }
  return t26;
}
function _temp(t0) {
  const {
    type
  } = t0;
  const now = Time.currentMillis();
  return {
    overview: {
      type,
      name: "",
      image: null
    },
    groups: {
      canView: null,
      canDrop: null,
      canVote: null,
      canChat: null,
      admin: null
    },
    chat: {
      enabled: true
    },
    dates: {
      submissionStartDate: now,
      votingStartDate: now,
      endDate: type === ApiWaveType.Rank ? now : null,
      firstDecisionTime: now,
      subsequentDecisions: [],
      isRolling: false
    },
    drops: {
      noOfApplicationsAllowedPerParticipant: null,
      requiredTypes: [],
      requiredMetadata: [],
      submissionStrategy: null,
      terms: null,
      signatureRequired: false,
      adminCanDeleteDrops: true
    },
    voting: {
      type: ApiWaveCreditType.TdhPlusXtdh,
      category: null,
      profileId: null,
      maxVotesPerIdentityPerDrop: null,
      winningThreshold: null,
      timeWeighted: {
        enabled: false,
        averagingInterval: 24,
        averagingIntervalUnit: "hours"
      }
    },
    outcomes: [],
    approval: {
      threshold: null,
      thresholdTimeMs: null,
      maxWinners: null
    }
  };
}